/* ShinShield buy box: pack (1/2/3 pairs) + colour & size per pair, one add-to-cart for all pairs.
   Bundle prices are applied in the cart by Shopify automatic discounts; this script only previews them.
   Integrates with Horizon's cart drawer through the standard CartLinesUpdateEvent. */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Formats cents with the shop's money format, e.g. "${{amount}}". */
function formatMoney(cents, format) {
  const value = Number(cents) / 100;
  const withDelimiters = (number, decimals, thousands = ',', decimal = '.') => {
    const [whole, fraction] = number.toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return withDelimiters(value, 0);
      case 'amount_with_comma_separator':
        return withDelimiters(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(value, 0, '.');
      default:
        return withDelimiters(value, 2);
    }
  });
}

class SsBuyBox extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('section');
    this.form = this.querySelector('[data-ss-form]');
    this.variants = JSON.parse(this.querySelector('[data-ss-variants]').textContent);
    this.unitPrice = Number(this.dataset.unitPrice);
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.pairEls = [...this.querySelectorAll('[data-pair]')];
    this.touched = new Set(); // pairs the shopper changed themselves (they stop following pair 1)

    this.form.addEventListener('change', this.onChange);
    // Tapping a pack (even the one already selected) brings the total + button into view if needed
    this.form.addEventListener('click', (event) => {
      if (event.target.closest('.ss-pack')) this.scheduleReveal();
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.addToCart();
    });

    this.querySelector('[data-open-size-guide]')?.addEventListener('click', () => this.openSizeGuide());
    this.setupSizeGuide();
    this.setupMedia();
    this.setupSticky();

    // Pre-select a bundle from ?bundle=N (links from other pages) or from on-page bundle links
    const fromUrl = Number(new URLSearchParams(window.location.search).get('bundle'));
    if (fromUrl) this.selectPack(fromUrl);
    document.addEventListener('ss:bundle-select', this.onBundleSelect);

    this.pairEls.forEach((pair) => this.refreshSizes(pair));
    this.update();

    // Coming back with the browser's Back button can restore an older pack choice:
    // re-read the form so the pickers, price and cart always match the selected pack
    window.addEventListener('pageshow', this.onPageShow);
  }

  onPageShow = () => {
    this.pairEls.forEach((pair) => this.refreshSizes(pair));
    this.update();
  };

  disconnectedCallback() {
    document.removeEventListener('ss:bundle-select', this.onBundleSelect);
    window.removeEventListener('pageshow', this.onPageShow);
    this.stickyObserver?.disconnect();
    this.overlayObserver?.disconnect();
  }

  onBundleSelect = (event) => this.selectPack(event.detail?.qty);

  /* ---------- State ---------- */

  get pairs() {
    return Number(this.form.querySelector('input[name="pack"]:checked')?.value || 1);
  }

  get packSaving() {
    return Number(this.form.querySelector('input[name="pack"]:checked')?.dataset.saving || 0);
  }

  pairState(pairEl) {
    const colourInput = pairEl.querySelector('input[type="radio"]:checked');
    return {
      colour: colourInput?.value || '',
      colourLabel: colourInput?.dataset.label || '',
      size: pairEl.querySelector('[data-size-select]').value,
    };
  }

  findVariant(colour, size) {
    return this.variants.find((variant) => variant.colour === colour && variant.size === size);
  }

  selectPack(qty, { reveal = false } = {}) {
    const input = this.form.querySelector(`input[name="pack"][value="${Number(qty)}"]`);
    if (!input) return;
    input.checked = true;
    this.update();
    if (reveal) this.scheduleReveal();
  }

  /* ---------- Events ---------- */

  onChange = (event) => {
    const target = event.target;
    const pairEl = target.closest('[data-pair]');

    if (pairEl) {
      const index = Number(pairEl.dataset.pair);
      if (index > 1) this.touched.add(index);
      if (target.type === 'radio') {
        this.refreshSizes(pairEl);
        this.showColour(target.value, target.dataset.label);
      }
      // New pairs follow pair 1 until the shopper changes them
      if (index === 1) this.syncFollowers();
      target.closest('.ss-pair')?.classList.remove('is-invalid');
      this.hideError();
    }

    this.update();
    if (target.name === 'pack') this.scheduleReveal();
  };

  syncFollowers() {
    const lead = this.pairEls[0];
    const { colour, size } = this.pairState(lead);
    this.pairEls.slice(1).forEach((pairEl) => {
      if (this.touched.has(Number(pairEl.dataset.pair))) return;
      const radio = pairEl.querySelector(`input[type="radio"][value="${CSS.escape(colour)}"]`);
      if (radio) radio.checked = true;
      this.refreshSizes(pairEl);
      const select = pairEl.querySelector('[data-size-select]');
      const option = [...select.options].find((o) => o.value === size && !o.disabled);
      select.value = option ? size : '';
    });
  }

  /* Disable sold-out sizes for the pair's colour */
  refreshSizes(pairEl) {
    const { colour } = this.pairState(pairEl);
    const select = pairEl.querySelector('[data-size-select]');
    [...select.options].forEach((option) => {
      if (!option.value) return;
      const variant = this.findVariant(colour, option.value);
      const available = Boolean(variant?.available);
      option.disabled = !available;
      const label = option.dataset.label || option.value;
      option.textContent = available ? label : `${label} (sold out)`;
    });
    if (select.selectedOptions[0]?.disabled) select.value = '';
  }

  /* ---------- Render ---------- */

  update() {
    const pairs = this.pairs;

    this.pairEls.forEach((pairEl, i) => {
      const visible = i < pairs;
      pairEl.hidden = !visible;
      pairEl.querySelector('[data-pair-title]').textContent = pairs > 1 ? `Pair ${i + 1}` : 'Your pair';
      pairEl.querySelector('[data-pair-colour]').textContent = this.pairState(pairEl).colourLabel;
    });

    const step2 = this.querySelector('[data-step2-label]');
    if (step2) step2.textContent = pairs > 1 ? step2.dataset.many : step2.dataset.one;

    const full = this.unitPrice * pairs;
    const total = full - this.packSaving;
    this.querySelector('[data-total]').textContent = this.money(total);
    const saveEl = this.querySelector('[data-total-save]');
    saveEl.hidden = this.packSaving <= 0;
    saveEl.textContent = this.packSaving > 0 ? `– you save ${this.money(this.packSaving)}` : '';

    const nudge = this.querySelector('[data-nudge]');
    const twoPack = this.form.querySelector('input[name="pack"][value="2"]');
    if (pairs === 1 && twoPack) {
      nudge.hidden = false;
      nudge.innerHTML = '';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ss-link';
      button.textContent = `Add a second pair: save ${this.money(Number(twoPack.dataset.saving))} + free shipping`;
      button.addEventListener('click', () => this.selectPack(2, { reveal: true }));
      nudge.append(button);
    } else {
      // The product page (compact layout) already shows free shipping under the packs and in the trust row
      nudge.hidden = Boolean(this.closest('.ss-buy--compact'));
      nudge.textContent = '✓ Free shipping included';
    }

    const label = pairs > 1 ? `Add ${pairs} pairs · ${this.money(total)}` : `Add to cart · ${this.money(total)}`;
    this.querySelector('[data-add-label]').textContent = label;

    this.updateSticky(total);
  }

  money(cents) {
    return formatMoney(cents, this.moneyFormat);
  }

  /* ---------- Add to cart ---------- */

  validate() {
    const missing = this.pairEls.slice(0, this.pairs).find((pairEl) => !this.pairState(pairEl).size);
    if (!missing) return true;
    const label = this.pairs > 1 ? `pair ${missing.dataset.pair}` : 'your pair';
    this.showError(`Please choose a size for ${label}.`);
    missing.classList.add('is-invalid');
    const select = missing.querySelector('[data-size-select]');
    this.scrollIntoViewIfNeeded(missing);
    select.focus({ preventScroll: true });
    return false;
  }

  lineItems() {
    // Group identical picks into one line. When the brand colour name differs from Shopify's
    // colour value (e.g. "Royal Blue" vs "Blue"), add it as a visible line property so the
    // cart, checkout and order show the name the shopper chose.
    const lines = new Map();
    for (const pairEl of this.pairEls.slice(0, this.pairs)) {
      const { colour, size, colourLabel } = this.pairState(pairEl);
      const variant = this.findVariant(colour, size);
      if (!variant || !variant.available) {
        this.showError(`${colourLabel} in ${size} is sold out. Please pick another size or colour.`);
        return null;
      }
      const renamed = colourLabel && colourLabel.toLowerCase() !== colour.toLowerCase();
      const key = `${variant.id}|${renamed ? colourLabel : ''}`;
      const line = lines.get(key) || { id: variant.id, quantity: 0, ...(renamed && { properties: { Colour: colourLabel } }) };
      line.quantity += 1;
      lines.set(key, line);
    }
    return [...lines.values()];
  }

  async addToCart() {
    if (this.busy || !this.validate()) return;
    const items = this.lineItems();
    if (!items) return;

    this.busy = true;
    const button = this.querySelector('[data-add]');
    const labelEl = this.querySelector('[data-add-label]');
    const previousLabel = labelEl.textContent;
    button.setAttribute('aria-busy', 'true');
    labelEl.textContent = 'Adding…';

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await this.fetchCart();

      if (!response.ok || data.status) {
        this.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items } });
        this.showError(data.description || data.message || 'Something went wrong. Please try again.');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ss-buy-box',
          sourceId: this.dataset.sectionId,
          itemCount: this.pairs,
          productId: this.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });

      labelEl.textContent = 'Added ✓';
      // No cart drawer on this page (cart set to "page"): go to the cart
      if (!document.querySelector('cart-drawer-component')) {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      }
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.showError('Network problem. Please check your connection and try again.');
    } finally {
      this.busy = false;
      button.removeAttribute('aria-busy');
      setTimeout(() => {
        labelEl.textContent = previousLabel;
        this.update();
      }, 1800);
    }
  }

  async fetchCart() {
    const component = document.querySelector('cart-items-component');
    if (component) {
      await customElements.whenDefined('cart-items-component');
      if (typeof component.fetchCartData === 'function') return component.fetchCartData();
    }
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  showError(message) {
    const el = this.querySelector('[data-error]');
    el.textContent = message;
    el.hidden = false;
  }

  hideError() {
    const el = this.querySelector('[data-error]');
    el.hidden = true;
  }

  scrollIntoViewIfNeeded(el) {
    const rect = el.getBoundingClientRect();
    if (rect.top < 80 || rect.bottom > window.innerHeight - 80) {
      el.scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'center' });
    }
  }

  /* ---------- Media ---------- */

  setupMedia() {
    this.media = this.section.querySelector('[data-ss-buy-media]');
    if (!this.media) return;
    this.media.querySelectorAll('[data-thumb]').forEach((thumb) =>
      thumb.addEventListener('click', () => {
        if (thumb.dataset.thumb === 'colour') {
          const { colour, colourLabel } = this.pairState(this.pairEls[0]);
          this.showColour(colour, colourLabel);
        } else {
          this.showSlide(this.media.querySelector(`[data-extra="${thumb.dataset.thumb}"]`), thumb);
        }
      })
    );
  }

  showColour(colour, label) {
    if (!this.media) return;
    const slide = this.media.querySelector(`[data-colour="${CSS.escape(colour)}"]`);
    this.showSlide(slide, this.media.querySelector('[data-thumb="colour"]'));
    const stageLabel = this.media.querySelector('[data-stage-label]');
    if (stageLabel) stageLabel.textContent = label;
    const colourThumbImg = this.media.querySelector('[data-thumb="colour"] img');
    const slideImg = slide?.querySelector('img');
    if (colourThumbImg && slideImg) colourThumbImg.src = slideImg.currentSrc || slideImg.src;
  }

  showSlide(slide, activeThumb) {
    if (!slide) return;
    this.media.querySelectorAll('.ss-buy__slide').forEach((el) => (el.dataset.active = String(el === slide)));
    this.media.querySelectorAll('[data-thumb]').forEach((el) => el.setAttribute('aria-pressed', String(el === activeThumb)));
    const stageLabel = this.media.querySelector('[data-stage-label]');
    if (stageLabel) stageLabel.hidden = !slide.dataset.colour;
  }

  /* ---------- Size guide ---------- */

  setupSizeGuide() {
    this.dialog = this.section.querySelector('[data-size-guide]');
    if (!this.dialog) return;
    this.dialog.querySelector('[data-close-dialog]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  openSizeGuide() {
    this.dialog?.showModal();
  }

  /* ---------- Sticky add-to-cart bar ---------- */

  /* Shown whenever the main Add to cart button is out of view (on the homepage, only after the
     hero). Hidden while the main button is visible, while the cart drawer is open, and while a
     cookie banner is showing. Adds exactly the same items as the main button. */
  setupSticky() {
    this.sticky = this.section.querySelector('[data-ss-sticky]');
    if (!this.sticky) return;
    document.body.append(this.sticky); // outside the section's stacking context so it stays fixed

    const hero = document.querySelector('.ss-hero');
    const addButton = this.querySelector('[data-add]');
    this.stickyState = { pastHero: !hero, buttonVisible: false };

    this.stickyObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === hero) this.stickyState.pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        // Counts as visible only when (almost) the whole button is on screen
        if (entry.target === addButton) this.stickyState.buttonVisible = entry.isIntersecting && entry.intersectionRatio >= 0.95;
      });
      this.renderSticky();
    }, { threshold: [0, 0.95, 1] });
    if (hero) this.stickyObserver.observe(hero);
    this.stickyObserver.observe(addButton);

    // Re-check when the cart drawer opens/closes or a cookie banner appears/disappears
    this.overlayObserver = new MutationObserver(() => this.renderSticky());
    this.overlayObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'style', 'class', 'hidden'], childList: true });

    this.sticky.querySelector('[data-sticky-btn]').addEventListener('click', () => this.addToCart());
  }

  overlayOpen() {
    const drawerOpen = Boolean(document.querySelector('cart-drawer-component')?.closest('dialog')?.open);
    const banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner__dialog');
    const bannerOpen = Boolean(banner && banner.offsetParent !== null && banner.getBoundingClientRect().height > 0);
    return drawerOpen || bannerOpen;
  }

  renderSticky() {
    if (!this.sticky) return;
    const show = this.stickyState.pastHero && !this.stickyState.buttonVisible && !this.overlayOpen();
    if (this.sticky.classList.contains('is-visible') === show) return;
    this.sticky.classList.toggle('is-visible', show);
    this.sticky.setAttribute('aria-hidden', String(!show));
    this.sticky.inert = !show;
    document.body.classList.toggle('ss-sticky-on', show);
    document.body.style.setProperty('--ss-sticky-h', `${this.sticky.offsetHeight}px`);
  }

  updateSticky(total) {
    if (!this.sticky) return;
    this.sticky.querySelector('[data-sticky-pack]').textContent = `${this.pairs} ${this.pairs > 1 ? 'pairs' : 'pair'}`;
    this.sticky.querySelector('[data-sticky-total]').textContent = this.money(total);
    const img = this.sticky.querySelector('[data-sticky-img]');
    const slide = this.media?.querySelector('.ss-buy__slide[data-active="true"][data-colour] img');
    if (img && slide) img.src = slide.currentSrc || slide.src;
  }

  /* ---------- Gentle scroll after choosing a pack ---------- */

  /* Only after the shopper picks a pack, and only if the total + button aren't fully visible:
     scroll just enough to show them, never so far that the chosen pack leaves the top. */
  scheduleReveal() {
    clearTimeout(this.revealTimer);
    this.revealTimer = setTimeout(() => requestAnimationFrame(() => this.revealSummary()), 60);
  }

  revealSummary() {
    const summaryTop = this.querySelector('.ss-buy__total');
    const button = this.querySelector('[data-add]');
    const pack = this.form.querySelector('input[name="pack"]:checked')?.closest('.ss-pack');
    if (!summaryTop || !button || !pack) return;

    const headerBottom = Math.max(0, document.querySelector('#header-group')?.getBoundingClientRect().bottom || 0);
    const viewBottom = window.innerHeight - 12;
    const buttonRect = button.getBoundingClientRect();
    if (buttonRect.bottom <= viewBottom && summaryTop.getBoundingClientRect().top >= headerBottom) return;

    const needed = buttonRect.bottom - viewBottom;
    const room = pack.getBoundingClientRect().top - (headerBottom + 12);
    const distance = Math.min(needed, Math.max(0, room));
    if (distance <= 0) return;

    const scroller = this.scroller();
    // 'instant' (not 'auto') so the theme's CSS smooth scrolling can't animate it for reduced-motion users
    scroller.scrollBy({ top: distance, behavior: reducedMotion() ? 'instant' : 'smooth' });
  }

  scroller() {
    // Horizon scrolls .page-wrapper on desktop and the window on phones
    let el = this.parentElement;
    while (el && el !== document.body) {
      const style = getComputedStyle(el);
      if (/(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight + 4) return el;
      el = el.parentElement;
    }
    return window;
  }
}

if (!customElements.get('ss-buy-box')) customElements.define('ss-buy-box', SsBuyBox);
